// src/pages/profile/ProfilePage.jsx
import { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { updateUserProfile } from "../../services/firestoreService";
import { useNavigate } from "react-router-dom";
import {
  requestNotificationPermission,
  notificationsSupported,
  notificationsEnabled,
} from "../../services/notificationService";
import Button from "../../components/ui/Button";
import Input  from "../../components/ui/Input";
import AppLayout from "../../components/layout/AppLayout";
import styles from "./ProfilePage.module.css";

const SA_PROVINCES = [
  "Gauteng",
  "Western Cape",
  "KwaZulu-Natal",
  "Eastern Cape",
  "Limpopo",
  "Mpumalanga",
  "North West",
  "Free State",
  "Northern Cape",
];

const JOB_TYPE_OPTIONS = [
  { value: "all",       label: "All jobs"             },
  { value: "freelance", label: "Freelance / Contract" },
  { value: "permanent", label: "Permanent"            },
];

export const INDUSTRY_OPTIONS = [
  { id: "tech",      label: "Tech & Dev"    },
  { id: "design",    label: "Design"        },
  { id: "marketing", label: "Marketing"     },
  { id: "finance",   label: "Finance"       },
  { id: "admin",     label: "Admin & Ops"   },
  { id: "sales",     label: "Sales"         },
  { id: "creative",  label: "Creative"      },
  { id: "trades",    label: "Trades"        },
  { id: "data",      label: "Data"          },
  { id: "education", label: "Education"     },
];

// Skills grouped by industry — users can browse by tab
const SKILL_GROUPS = {
  "Tech & Dev": [
    "React", "Vue.js", "Angular", "Node.js", "Python", "PHP", "Laravel",
    "Django", "TypeScript", "JavaScript", "Flutter", "React Native", "Java",
    "C#", ".NET", "Ruby on Rails", "WordPress", "Shopify", "SQL", "MongoDB",
    "Firebase", "AWS", "Docker", "Kubernetes", "GraphQL", "REST APIs",
  ],
  "Design": [
    "UI/UX Design", "Graphic Design", "Figma", "Adobe XD", "Photoshop",
    "Illustrator", "InDesign", "Logo Design", "Brand Identity", "Motion Graphics",
    "3D Design", "After Effects", "Premiere Pro", "Canva", "Sketch",
  ],
  "Marketing": [
    "Digital Marketing", "SEO", "Google Ads", "Facebook Ads", "TikTok Ads",
    "Social Media Management", "Content Strategy", "Email Marketing",
    "Copywriting", "Blogging", "Influencer Marketing", "Google Analytics",
    "HubSpot", "Mailchimp",
  ],
  "Sales & Business": [
    "Sales", "Business Development", "Account Management", "CRM",
    "Lead Generation", "Cold Calling", "Negotiation", "Proposal Writing",
    "Salesforce", "Pipedrive",
  ],
  "Finance": [
    "Bookkeeping", "Accounting", "Tax", "Payroll", "Auditing",
    "Financial Reporting", "Xero", "Sage", "QuickBooks",
    "Excel / Spreadsheets", "Financial Modelling",
  ],
  "Data & Analytics": [
    "Data Analysis", "Power BI", "Tableau", "Excel", "SQL",
    "Python (Data)", "R", "Machine Learning", "Data Visualization",
    "Business Intelligence",
  ],
  "Admin & Operations": [
    "Virtual Assistant", "Office Administration", "Project Management",
    "Scheduling", "Data Entry", "Customer Service", "CRM Management",
    "Document Management", "Procurement",
  ],
  "Creative": [
    "Photography", "Videography", "Video Editing", "Podcast Production",
    "Music Production", "Voice Over", "Animation", "Illustration",
    "Scriptwriting", "Storyboarding",
  ],
  "Trades": [
    "Plumbing", "Electrical", "Carpentry", "Painting", "Tiling",
    "Welding", "HVAC", "Solar Installation", "Garden Services",
    "Cleaning", "Construction", "Civil Engineering",
  ],
  "Education": [
    "Tutoring", "Online Teaching", "Curriculum Design",
    "Corporate Training", "Coaching", "Mentoring", "E-Learning",
  ],
};

const GROUP_NAMES = Object.keys(SKILL_GROUPS);

export default function ProfilePage() {
  const { currentUser, userProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [displayName,        setDisplayName]        = useState(userProfile?.displayName        || "");
  const [bio,                setBio]                = useState(userProfile?.bio                || "");
  const [location,           setLocation]           = useState(userProfile?.location           || "");
  // provinces: array — migrate old single "province" string automatically
  const [provinces,          setProvinces]          = useState(
    userProfile?.provinces?.length
      ? userProfile.provinces
      : (userProfile?.province ? [userProfile.province] : [])
  );
  const [jobTypePreference,  setJobTypePreference]  = useState(userProfile?.jobTypePreference  || "all");
  const [preferredIndustry,  setPreferredIndustry]  = useState(userProfile?.preferredIndustry  || "");
  const [skills,             setSkills]             = useState(userProfile?.skills             || []);
  const [activeGroup,        setActiveGroup]        = useState(GROUP_NAMES[0]);
  const [saving,             setSaving]             = useState(false);
  const [saved,              setSaved]              = useState(false);
  const [error,              setError]              = useState("");

  const [notifStatus,  setNotifStatus]  = useState(() => {
    if (!notificationsSupported()) return "unsupported";
    return notificationsEnabled() ? "granted" : "prompt";
  });
  const [notifLoading, setNotifLoading] = useState(false);

  const toggleProvince = (p) =>
    setProvinces(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);

  const handleEnableNotifications = async () => {
    setNotifLoading(true);
    const result = await requestNotificationPermission(currentUser.uid);
    setNotifLoading(false);
    if (result.ok) {
      setNotifStatus("granted");
    } else if (result.reason === "denied") {
      setNotifStatus("denied");
    }
  };

  const toggleSkill = (skill) => {
    setSkills(prev =>
      prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]
    );
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) return setError("Display name is required.");
    setError(""); setSaving(true);
    try {
      await updateUserProfile(currentUser.uid, {
        displayName, bio, location,
        provinces, province: provinces[0] || "",
        jobTypePreference, preferredIndustry, skills,
        email: currentUser.email || "",
      });
      await refreshProfile();
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } catch (err) {
      setError("Failed to save profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
    {saved && (
      <div className={styles.toast}>
        Profile saved successfully!
      </div>
    )}
    <div className={styles.page}>
      <main className={styles.main}>
        <div className={styles.card}>
          <div className={styles.avatar}>
            {userProfile?.photoURL
              ? <img src={userProfile.photoURL} alt="avatar" />
              : <span>{(displayName || "?")[0].toUpperCase()}</span>
            }
          </div>

          <h1 className={styles.heading}>Your Profile</h1>
          <p className={styles.sub}>Your preferences are applied to your job feed automatically</p>

          {error && <div className={styles.errorBanner}>{error}</div>}

          <form onSubmit={handleSave} className={styles.form}>
            <Input
              label="Display Name"
              id="displayName"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Your full name"
              required
            />

            <div className={styles.field}>
              <label className={styles.label}>Provinces</label>
              <div className={styles.typeOptionGroup} style={{ flexWrap: "wrap", gap: 8 }}>
                {SA_PROVINCES.map(p => (
                  <button
                    key={p}
                    type="button"
                    className={`${styles.typeOption} ${provinces.includes(p) ? styles.typeOptionActive : ""}`}
                    onClick={() => toggleProvince(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <p className={styles.fieldHint}>
                {provinces.length === 0
                  ? "Select one or more provinces to filter your job feed"
                  : `Showing jobs in: ${provinces.join(", ")}`}
              </p>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Job Type Preference</label>
              <div className={styles.typeOptionGroup}>
                {JOB_TYPE_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`${styles.typeOption} ${jobTypePreference === opt.value ? styles.typeOptionActive : ""}`}
                    onClick={() => setJobTypePreference(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className={styles.fieldHint}>Your job feed will default to this type</p>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Industry Focus</label>
              <div className={styles.typeOptionGroup} style={{ flexWrap: "wrap", gap: 8 }}>
                {INDUSTRY_OPTIONS.map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`${styles.typeOption} ${preferredIndustry === opt.id ? styles.typeOptionActive : ""}`}
                    onClick={() => setPreferredIndustry(prev => prev === opt.id ? "" : opt.id)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className={styles.fieldHint}>Sets the default category tab in your job feed</p>
            </div>

            <Input
              label="City / Town"
              id="location"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="e.g. Pretoria"
            />
            <div className={styles.field}>
              <label className={styles.label}>Bio</label>
              <textarea
                className={styles.textarea}
                value={bio}
                onChange={e => setBio(e.target.value)}
                placeholder="Tell clients what you do and what makes you great…"
                rows={4}
              />
            </div>

            {/* ── Skills with industry tabs ── */}
            <div className={styles.field}>
              <label className={styles.label}>Skills</label>

              {/* Selected skill chips summary */}
              {skills.length > 0 && (
                <div className={styles.selectedSkills}>
                  {skills.map(skill => (
                    <button
                      key={skill}
                      type="button"
                      className={styles.selectedChip}
                      onClick={() => toggleSkill(skill)}
                    >
                      {skill} ✕
                    </button>
                  ))}
                </div>
              )}

              {/* Industry tab nav */}
              <div className={styles.groupTabs}>
                {GROUP_NAMES.map(group => (
                  <button
                    key={group}
                    type="button"
                    className={`${styles.groupTab} ${activeGroup === group ? styles.groupTabActive : ""}`}
                    onClick={() => setActiveGroup(group)}
                  >
                    {group}
                    {SKILL_GROUPS[group].some(s => skills.includes(s)) && (
                      <span className={styles.groupDot} />
                    )}
                  </button>
                ))}
              </div>

              {/* Skills for active group */}
              <div className={styles.skillGrid}>
                {SKILL_GROUPS[activeGroup].map(skill => (
                  <button
                    key={skill}
                    type="button"
                    className={`${styles.skillTag} ${skills.includes(skill) ? styles.active : ""}`}
                    onClick={() => toggleSkill(skill)}
                  >
                    {skill}
                  </button>
                ))}
              </div>

              <p className={styles.skillHint}>
                {skills.length === 0
                  ? "Select skills to personalise your job feed"
                  : `${skills.length} skill${skills.length !== 1 ? "s" : ""} selected`}
              </p>
            </div>

            {/* ── Job alerts ── */}
            {notifStatus !== "unsupported" && (
              <div className={styles.field}>
                <label className={styles.label}>Job Alerts</label>
                {notifStatus === "granted" ? (
                  <div className={styles.notifOn}>
                    <span className={styles.notifDot} />
                    Push notifications on — you'll be alerted when new jobs match your industry
                  </div>
                ) : notifStatus === "denied" ? (
                  <div className={styles.notifDenied}>
                    Notifications blocked in your browser. Enable them in browser settings then reload.
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles.notifBtn}
                    onClick={handleEnableNotifications}
                    disabled={notifLoading}
                  >
                    {notifLoading ? "Enabling…" : "🔔 Enable job alerts"}
                  </button>
                )}
                <p className={styles.fieldHint}>
                  Get notified when new jobs in your chosen industry are posted
                </p>
              </div>
            )}

            <Button type="submit" fullWidth loading={saving}>
              Save Profile
            </Button>
          </form>
        </div>
      </main>
    </div>
    </AppLayout>
  );
}
